/**
 * WidgetRenderer - Factory that renders widgets based on their type
 *
 * Each widget type maps to a component that receives the widget's config.
 */

import React from 'react';
import type { Widget, WidgetType, WidgetConfigMap } from '../../types/widgets';
import { TaskListWidget } from './TaskListWidget';
import { CalendarBarWidget } from './CalendarBarWidget';
import { CalendarFullWidget } from './CalendarFullWidget';
import { DayScheduleWidget } from './DayScheduleWidget';
import { TimelineWidget } from './TimelineWidget';
import { WeeklyReviewWidget } from './WeeklyReviewWidget';
import { MonthlyReviewWidget } from './MonthlyReviewWidget';
import { YearlyReviewWidget } from './YearlyReviewWidget';
import { AIChatWidget } from './AIChatWidget';
import { HabitTrackerWidget } from './HabitTrackerWidget';
import { GoalsWidget } from './GoalsWidget';
import { NotesWidget } from './NotesWidget';
import { DailyJournalWidget } from './DailyJournalWidget';
import { PomodoroWidget } from './PomodoroWidget';
import { ActivityFeedWidget } from './ActivityFeedWidget';

// Widget component type
type WidgetComponent<T extends WidgetType> = React.FC<{
  config: WidgetConfigMap[T];
  widgetId: string;
  windowId: string;
}>;

// Registry mapping widget types to components
const WIDGET_REGISTRY: { [K in WidgetType]: WidgetComponent<K> } = {
  'task-list': TaskListWidget,
  'calendar-bar': CalendarBarWidget,
  'calendar-full': CalendarFullWidget,
  'day-schedule': DayScheduleWidget,
  'timeline': TimelineWidget,
  'weekly-review': WeeklyReviewWidget,
  'monthly-review': MonthlyReviewWidget,
  'yearly-review': YearlyReviewWidget,
  'ai-chat': AIChatWidget,
  'habit-tracker': HabitTrackerWidget,
  'goals': GoalsWidget,
  'notes': NotesWidget,
  'daily-journal': DailyJournalWidget,
  'pomodoro': PomodoroWidget,
  'activity-feed': ActivityFeedWidget,
};

// Human-readable labels for widget types
export const WIDGET_LABELS: Record<WidgetType, string> = {
  'task-list': 'Task List',
  'calendar-bar': 'Calendar Bar',
  'calendar-full': 'Calendar',
  'day-schedule': 'Day Schedule',
  'timeline': 'Timeline',
  'weekly-review': 'Weekly Review',
  'monthly-review': 'Monthly Review',
  'yearly-review': 'Yearly Review',
  'ai-chat': 'AI Chat',
  'habit-tracker': 'Habit Tracker',
  'goals': 'Goals',
  'notes': 'Notes',
  'daily-journal': 'Daily Journal',
  'pomodoro': 'Pomodoro',
  'activity-feed': 'Activity Feed',
};

// Icons for widget types (lucide icon names)
export const WIDGET_ICONS: Record<WidgetType, string> = {
  'task-list': 'list-todo',
  'calendar-bar': 'calendar-range',
  'calendar-full': 'calendar',
  'day-schedule': 'clock',
  'timeline': 'git-branch',
  'weekly-review': 'calendar-days',
  'monthly-review': 'calendar-check',
  'yearly-review': 'target',
  'ai-chat': 'bot',
  'habit-tracker': 'repeat',
  'goals': 'target',
  'notes': 'sticky-note',
  'daily-journal': 'book-open',
  'pomodoro': 'timer',
  'activity-feed': 'activity',
};

interface WidgetRendererProps {
  widget: Widget;
  windowId: string;
}

export function WidgetRenderer({ widget, windowId }: WidgetRendererProps) {
  const Component = WIDGET_REGISTRY[widget.type] as React.FC<{
    config: WidgetConfigMap[typeof widget.type];
    widgetId: string;
    windowId: string;
  }>;

  if (!Component) {
    return (
      <div className="flex items-center justify-center h-full text-gray-400">
        Unknown widget type: {widget.type}
      </div>
    );
  }

  return <Component config={widget.config} widgetId={widget.id} windowId={windowId} />;
}
